import React from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Link } from "@mui/material";
import { THEMES } from "../content/themes";
import { Lang } from "../content/mutualiteiten";
import NotFound from "./NotFound";

/** Thema-overzichtspagina (/thema/:slug) — informatief en navigerend.
 *  De copy komt uit content/themes.ts en volgt de MDR-wordingregels:
 *  geen interpretatie van symptomen of ernst, geen testframing. */
export default function Theme() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = ((i18n.language || "nl").slice(0, 2) as Lang);
  const theme = THEMES.find((th) => th.slug === slug);
  if (!theme) return <NotFound />;
  const c = theme.content[lang] || theme.content.nl;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2.25rem", sm: "2.75rem" }, mb: 2 }}>{c.title}</Typography>
        <Typography sx={{ fontSize: "1.15rem", mb: 4, color: "text.secondary", lineHeight: 1.7 }}>{c.lead}</Typography>

        {c.sections.map((s) => (
          <Box key={s.heading} sx={{ mb: 3.5 }}>
            <Typography variant="h2" sx={{ fontSize: "1.4rem", mb: 1.5 }}>{s.heading}</Typography>
            {s.paragraphs.map((p, i) => (
              <Typography key={i} sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.7 }}>{p}</Typography>
            ))}
            {s.bullets && (
              <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3 }}>
                {s.bullets.map((b, i) => (
                  <Typography key={i} component="li" sx={{ mb: 0.75, color: "text.secondary", lineHeight: 1.7 }}>{b}</Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}

        {/* Vast crisis-vangnet op elke themapagina. */}
        <Box sx={{ p: 3, mb: 4, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("themePage.crisisTitle")}</Typography>
          <Typography sx={{ m: 0, color: "text.secondary" }}>
            {t("themePage.crisisBody")}{" "}
            <Link href="tel:1813">1813</Link> · <Link href="tel:106">Tele-Onthaal 106</Link> · <Link href="tel:112">112</Link>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <Button variant="contained" size="large" onClick={() => navigate("/start")} sx={{ bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}>
            {t("themePage.ctaMatch")}
          </Button>
          <Button component={RouterLink} to="/zelfhulp" sx={{ textTransform: "none" }}>
            {t("themePage.ctaSelfHelp")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
